'use client';

import { useRouter } from 'next/navigation';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';

import { useGlobalModalContext } from '@abg/web-core/context/global-modal';
import { usePageContext } from '@abg/web-core/context/page/client-provider';

import { userStoreSelector } from '../../store';

import { ConcurConnectDialog } from './ConcurConnectDialog';
import { COMPANY_DECLINED_URL_PARAMETER } from './constants';
import type { ConcurConnectDialogWrapperProps } from './types';
import { useConcurConnectDialog } from './use-concur-connect-dialog';

export const ConcurConnectDialogWrapper: React.FC<
  ConcurConnectDialogWrapperProps
> = ({ i18n }) => {
  const router = useRouter();
  const { navigationLinks } = usePageContext();
  const isAuthenticated = userStoreSelector.use.isAuthenticated();
  const { openRegularModal, closeRegularModal } = useGlobalModalContext();
  const modalActions = useRef({
    openRegularModal,
    closeRegularModal,
  });
  const { view, setView, companyName, saveConnectState, isHomePage } =
    useConcurConnectDialog();

  const resetView = useCallback(() => {
    setView(null);
  }, [setView]);

  const onAgree = useCallback(() => {
    saveConnectState(true);

    setView({
      isConfirmation: true,
    });
  }, [saveConnectState, setView]);

  const onDecline = useCallback(() => {
    saveConnectState(false);
    setView(null);

    router.push(
      `${navigationLinks.accountDashboard}?${COMPANY_DECLINED_URL_PARAMETER}`,
    );
  }, [saveConnectState, router, navigationLinks.accountDashboard, setView]);

  const dialogProps = useMemo(
    () => ({
      i18n,
      resetView,
      onAgree,
      onDecline,
      view,
      companyName,
    }),
    [i18n, resetView, onAgree, onDecline, view, companyName],
  );

  useEffect(() => {
    if (
      (isHomePage &&
        isAuthenticated &&
        (view?.isStart || view?.isConfirmation)) ||
      view?.isDeclined
    ) {
      void modalActions.current.openRegularModal?.(
        ConcurConnectDialog,
        dialogProps,
      );
    }
  }, [dialogProps, isAuthenticated, isHomePage, view]);

  return null;
};
